// GFC Policy Service - Enhanced API integration for GFC (Granular Federation Controls)
// Connects to backend GFC endpoints for advanced Teams policy management

import { apiClient } from './apiClient';

// GFC Types matching backend interfaces
export type CommunicationWithExternalOrgs = 
  | 'OrganizationDefault'        // Use organization-wide default settings
  | 'AllowAllExternalDomains'    // Allow all external domains
  | 'AllowSpecificExternalDomains' // Allow only specific domains (allowlist)
  | 'BlockSpecificExternalDomains' // Block specific domains (blocklist) 
  | 'BlockAllExternalDomains';   // Block all external domains

export interface ExistingTeamsPolicy {
  identity: string;               // Policy identity/ID
  displayName?: string;           // Human-readable policy name
  policyType: 'ExternalAccess' | 'CallingPolicy' | 'MeetingPolicy' | 'Unknown';
  
  // Current federation settings
  enableFederationAccess: boolean;
  allowedDomains?: string[];      // Current allowed domains
  blockedDomains?: string[];      // Current blocked domains
  
  // GFC readiness assessment
  gfcCompatible: boolean;         // Can this policy be migrated to GFC?
  communicationMode?: CommunicationWithExternalOrgs; // Detected/inferred mode
  
  // ADM management status
  isADMManaged: boolean;          // Is this policy managed by our ADM tool?
  managedByOther?: string;        // If managed by another tool/system
  
  // Policy assignment information
  assignedUsers?: number;         // Number of users assigned to this policy
  assignedGroups?: Array<{        // Entra ID groups assigned to this policy with rank
    groupId: string;
    displayName?: string;
    rank?: number;
    assignedAt?: string;
  }>;
  
  // Metadata
  lastModified?: string;          // Last modification timestamp
  createdBy?: string;             // Who created the policy
}

export interface GFCPrerequisiteCheck {
  // GFC availability check
  gfcAvailable: boolean;                  // Is GFC private preview enabled?
  gfcTestResult?: string;                 // Details of GFC availability test
  
  // Tenant-level federation settings
  allowFederatedUsers: boolean;           // Must be true for GFC to work
  tenantDefaultCommunication?: CommunicationWithExternalOrgs;
  
  // Domain limits and usage
  currentDomainCount: {
    allowed: number;                      // Current allowed domains across all policies
    blocked: number;                      // Current blocked domains across all policies
    total: number;                        // Total domains configured
  };
  
  // GFC limits (from private preview documentation)
  limits: {
    maxDomainsPerPolicy: number;          // 100 per policy
    maxTenantWideDomains: number;         // 4000 tenant-wide
    remainingCapacity: number;            // Available domain slots
  };
  
  // Readiness assessment
  gfcReady: boolean;                      // Can GFC be enabled?
  blockers: string[];                     // What's preventing GFC enablement
  warnings: string[];                     // Non-blocking issues to address
  
  // Validation timestamp
  checkedAt: string;                      // ISO timestamp
  checkedBy?: string;                     // User who ran the check
}

export interface GFCPolicyCreationOptions {
  // Basic policy info
  policyName: string;
  displayName?: string;
  description?: string;
  
  // GFC Communication Mode Selection
  communicationMode: CommunicationWithExternalOrgs;
  
  // Domain configuration (respecting limits)
  domains: {
    allowed: string[];                    // Max 100 per policy
    blocked: string[];                    // Max 100 per policy
  };
  
  // Risk and compliance settings
  riskCategory?: 'High' | 'Medium' | 'Low' | 'Trusted';
  complianceRegion?: 'EU' | 'US' | 'APAC' | 'Global';
  
  // Assignment options
  assignToGroups?: string[];              // Entra ID group IDs
  assignToUsers?: string[];               // User IDs for direct assignment
  
  // ADM-specific options
  linkToODPolicy?: string;                // OD Policy ID if creating from OD Policy
  syncWithFeed?: boolean;                 // Should this policy sync with domain feeds
  
  // Validation options
  validatePrerequisites?: boolean;        // Check prerequisites before creation
  optimizeDomainLimits?: boolean;         // Auto-split if over 100 domains
  
  // Preview mode
  previewOnly?: boolean;                  // Generate preview without creating
}

export interface DomainLimitOptimization {
  // Analysis of domain list
  originalDomainCount: number;
  exceedsLimit: boolean;
  
  // Optimization strategies
  strategies: Array<{
    strategy: 'split_policies' | 'remove_duplicates' | 'consolidate_wildcards' | 'risk_based_filtering';
    description: string;
    domainReduction: number;              // How many domains this strategy removes
    implementation: any;                  // Strategy-specific implementation details
  }>;
  
  // Recommended approach
  recommendedStrategy?: string;
  optimizedDomainCount: number;
  
  // If splitting into multiple policies
  splitPolicies?: Array<{
    policyName: string;
    domains: string[];
    domainCount: number;
  }>;
  
  // Validation
  meetsLimits: boolean;
  timestamp: string;
}

export interface GFCPolicyResult {
  success: boolean;
  teamsPolicyId?: string;
  message: string;
  error?: string;
  preview?: {
    policyConfiguration: any;
    domainCount: number;
    estimatedCreationTime: number;
  };
}

class GFCPolicyService {
  
  /**
   * Discover existing Teams policies for GFC migration assessment
   */
  async getExistingTeamsPolicies(): Promise<{
    policies: ExistingTeamsPolicy[];
    summary: {
      totalPolicies: number;
      gfcCompatible: number;
      admManaged: number;
      externalAccess: number;
      communicationModes: Record<string, number>;
    };
  }> {
    try {
      const response = await apiClient.get<{
        policies: ExistingTeamsPolicy[];
        summary: any;
      }>('/policies/existing-teams-policies');
      
      return response.data;
    } catch (error) {
      // Silently handle errors
      throw new Error('Failed to discover existing Teams policies. Please check your Teams Admin permissions.');
    }
  }

  /**
   * Check GFC prerequisites and tenant readiness
   */
  async checkGFCPrerequisites(): Promise<GFCPrerequisiteCheck> {
    try {
      const response = await apiClient.get<GFCPrerequisiteCheck>('/policies/gfc-prerequisites');
      return response.data;
    } catch (error) {
      // Silently handle errors
      throw new Error('Failed to check GFC readiness. Please verify your Teams Admin permissions.');
    }
  }

  /**
   * Create or preview a GFC-enabled Teams policy
   */
  async createGFCPolicy(options: GFCPolicyCreationOptions): Promise<GFCPolicyResult> {
    try {
      const response = await apiClient.post<{
        gfcPolicy: any;
        result: GFCPolicyResult;
        message: string;
      }>('/policies/gfc-policy', options);
      
      return response.data.result;
    } catch (error) {
      // Silently handle errors
      throw new Error('Failed to create GFC policy. Please check your configuration and try again.');
    }
  }

  /**
   * Optimize domain lists for GFC compliance
   */
  async optimizeDomains(domains: string[]): Promise<{
    optimization: DomainLimitOptimization;
    recommendations: {
      meetsLimits: boolean;
      recommendedStrategy: string | undefined;
      summary: string;
    };
  }> {
    try {
      const response = await apiClient.post<{
        optimization: DomainLimitOptimization;
        recommendations: {
          meetsLimits: boolean;
          recommendedStrategy: string | undefined;
          summary: string;
        };
      }>('/policies/optimize-domains', { domains });
      
      return response.data;
    } catch (error) {
      // Silently handle errors
      throw new Error('Failed to optimize domain list. Please verify domain formats and try again.');
    }
  }

  /**
   * Get communication mode descriptions for UI display
   */
  getCommunicationModeDescriptions(): Record<CommunicationWithExternalOrgs, {
    title: string;
    description: string;
    riskLevel: 'low' | 'medium' | 'high';
    useCase: string;
  }> {
    return {
      'OrganizationDefault': {
        title: 'Organization Default',
        description: 'Use your organization\'s current Teams federation settings',
        riskLevel: 'medium',
        useCase: 'Inherit existing corporate policies'
      },
      'AllowAllExternalDomains': {
        title: 'Allow All External Domains',
        description: 'Allow communication with any external domain',
        riskLevel: 'high',
        useCase: 'Maximum flexibility for external collaboration'
      },
      'AllowSpecificExternalDomains': {
        title: 'Allow Specific Domains (Allowlist)',
        description: 'Only allow communication with specified trusted domains',
        riskLevel: 'low',
        useCase: 'Financial services with known banking partners'
      },
      'BlockSpecificExternalDomains': {
        title: 'Block Specific Domains (Blocklist)',
        description: 'Allow all external domains except those specifically blocked',
        riskLevel: 'medium',
        useCase: 'Block known risk domains while maintaining openness'
      },
      'BlockAllExternalDomains': {
        title: 'Block All External Domains',
        description: 'Block all external domain communication',
        riskLevel: 'low',
        useCase: 'Maximum security for internal-only organizations'
      }
    };
  }

  /**
   * Validate domain format
   */
  validateDomainFormat(domain: string): boolean {
    const domainRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return domainRegex.test(domain);
  }

  /**
   * Get risk category color coding for UI
   */
  getRiskCategoryColors(): Record<string, { background: string; border: string; text: string }> {
    return {
      'Trusted': { background: '#DDF4FF', border: '#0078D4', text: '#005A9E' },
      'Low': { background: '#F3F9FC', border: '#107C10', text: '#0B5394' },
      'Medium': { background: '#FFF4CE', border: '#FF8C00', text: '#8A5700' },
      'High': { background: '#FDE7E9', border: '#D13438', text: '#A4262C' }
    };
  }

  /**
   * Estimate policy creation time based on complexity
   */
  estimateCreationTime(options: GFCPolicyCreationOptions): number {
    const baseTime = 30; // seconds
    const domainCount = (options.domains.allowed?.length || 0) + (options.domains.blocked?.length || 0);
    const groupAssignments = options.assignToGroups?.length || 0;
    
    return baseTime + (domainCount * 0.5) + (groupAssignments * 5);
  }

  /**
   * Get compliance region requirements
   */
  getComplianceRegionRequirements(): Record<string, {
    name: string;
    requirements: string[];
    restrictions: string[];
  }> {
    return {
      'EU': {
        name: 'European Union',
        requirements: ['GDPR compliance', 'Data residency in EU'],
        restrictions: ['No data transfer outside EU without adequacy decision']
      },
      'US': {
        name: 'United States',
        requirements: ['SOX compliance for financial institutions', 'FINRA regulations'],
        restrictions: ['Export control compliance', 'Government contractor restrictions']
      },
      'APAC': {
        name: 'Asia Pacific',
        requirements: ['Local data protection laws', 'Regional banking regulations'],
        restrictions: ['China firewall considerations', 'Singapore MAS requirements']
      },
      'Global': {
        name: 'Global/Multi-Region',
        requirements: ['Cross-border data transfer agreements', 'Multi-jurisdiction compliance'],
        restrictions: ['Lowest common denominator restrictions apply']
      }
    };
  }
}

export const gfcPolicyService = new GFCPolicyService();